<template>
  <base-dialog :value="value" max-width="700" @input="onVisibleChange">
    <v-card class="device-relocation overflow-hidden" data-testid="device-relocation-dialog">
      <div class="d-flex device-relocation__panes">
        <div class="min-w-0 device-relocation__input">
          <p class="mb-2 device-relocation__title">{{ $t('label.title') }}</p>
          <p class="mb-5 device-relocation__lead">
            {{ $t('msg.lead', { name: sourceName, deviceId: displayDeviceId }) }}
          </p>
          <p class="mb-2 font-weight-bold primary--text device-relocation__field-label">
            {{ $t('field.destination_label') }}
          </p>
          <v-radio-group
            v-model="mode"
            row
            hide-details
            class="mt-0 mb-6 device-relocation__mode"
            data-testid="device-relocation-mode"
          >
            <v-radio
              :label="$t('field.mode_new')"
              :value="MODE_NEW"
              color="primary"
              data-testid="device-relocation-mode-new"
            />
            <v-radio
              :label="$t('field.mode_inactive')"
              :value="MODE_INACTIVE"
              color="primary"
              data-testid="device-relocation-mode-inactive"
            />
          </v-radio-group>

          <new-monitoring-point-form
            v-if="isNewMode"
            :key="`new-${formKey}`"
            @change="onNewFormChange"
          />
          <inactive-monitoring-point-list
            v-else
            :key="`inactive-${formKey}`"
            :candidates="destinationCandidates"
            :loading="candidatesLoading"
            @change="onDestinationChange"
          />

          <v-card-actions class="px-0">
            <v-spacer />
            <dialog-action
              data-testid="device-relocation-cancel"
              @click="cancel"
            >
              {{ $t('button.cancel') }}
            </dialog-action>
            <dialog-action
              :loading="loading"
              :disabled="!canSubmit || loading"
              data-testid="device-relocation-submit"
              @click="submit"
            >
              {{ $t('button.submit') }}
            </dialog-action>
          </v-card-actions>
        </div>

        <div class="device-relocation__notice" data-testid="device-relocation-notice">
          <v-alert
            dense
            text
            type="warning"
            :icon="false"
            class="device-relocation__warning"
          >
            <p class="font-weight-bold mb-1">{{ $t('msg.notice_head') }}</p>
            <ol class="ma-0 pl-5">
              <li>{{ $t('msg.notice_step_stop') }}</li>
              <li>{{ $t('msg.notice_step_format') }}</li>
            </ol>
          </v-alert>
          <p class="text-caption font-weight-bold mb-1 device-relocation__after-head">
            {{ $t('msg.after_head') }}
          </p>
          <ul class="ma-0 device-relocation__after-list">
            <li>{{ $t('msg.after_keep_device') }}</li>
            <li>{{ $t('msg.after_source_inactive') }}</li>
            <li>{{ $t('msg.after_data_destination') }}</li>
          </ul>
        </div>
      </div>
    </v-card>
  </base-dialog>
</template>

<i18n lang="yaml">
ja:
  label:
    title: 'センサーの移設'
  field:
    destination_label: '移設先のモニタリングポイント'
    mode_new: '新しく作成'
    mode_inactive: '休止中から選ぶ'
  msg:
    lead: '「{name}」のセンサー（ID: {deviceId}）を、別のモニタリングポイントへ移設します。'
    notice_head: '⚠ 操作を実行する前に必ず現地作業を完了してください'
    notice_step_stop: 'センサーを停止する'
    notice_step_format: 'センサー内のデータをフォーマット（初期化）する'
    after_head: 'この操作を実行すると'
    after_keep_device: '現在のモニタリングポイントで使用しているセンサーを、移設先のモニタリングポイントで引き続き利用します'
    after_source_inactive: '現在のモニタリングポイントは休止中になります'
    after_data_destination: '以降センサーから受信するデータは移設先モニタリングポイントに蓄積されます'
  button:
    cancel: 'キャンセル'
    submit: 'センサーを移設する'
en:
  label:
    title: 'Relocate Sensor'
  field:
    destination_label: 'Destination Monitoring Point'
    mode_new: 'Create new'
    mode_inactive: 'Select from inactive ones'
  msg:
    lead: "Relocate the sensor (ID: {deviceId}) of '{name}' to another Monitoring Point."
    notice_head: '⚠ Complete the on-site work before performing this operation'
    notice_step_stop: 'Stop the sensor'
    notice_step_format: 'Format (initialize) the data in the sensor'
    after_head: 'When you perform this operation'
    after_keep_device: >-
      The sensor currently used by this Monitoring Point will continue to be used
      by the destination Monitoring Point
    after_source_inactive: 'This Monitoring Point will become inactive'
    after_data_destination: 'Data received from the sensor will be stored in the destination Monitoring Point'
  button:
    cancel: 'Cancel'
    submit: 'Relocate Sensor'
</i18n>

<script>
import BaseDialog from '@/components/atoms/BaseDialog';
import DialogAction from '@/components/atoms/DialogAction';
import NewMonitoringPointForm from './NewMonitoringPointForm';
import InactiveMonitoringPointList from './InactiveMonitoringPointList';

const MODE_NEW = 'new';
const MODE_INACTIVE = 'inactive';

const EMPTY_NEW_FORM = { name: '', copyShare: false, valid: false };

export default {
  name: 'device-relocation-dialog',
  inheritAttrs: false,
  components: {
    BaseDialog,
    DialogAction,
    NewMonitoringPointForm,
    InactiveMonitoringPointList,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    candidatesLoading: {
      type: Boolean,
      default: false,
    },
    destinationCandidates: {
      type: Array,
      default: () => [],
    },
    sourceName: {
      type: String,
      default: '',
    },
    sourceDeviceId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      MODE_NEW,
      MODE_INACTIVE,
      mode: MODE_NEW,
      formKey: 0,
      newForm: { ...EMPTY_NEW_FORM },
      destinationId: null,
    };
  },
  computed: {
    isNewMode() {
      return this.mode === MODE_NEW;
    },
    displayDeviceId() {
      return this.sourceDeviceId != null ? this.sourceDeviceId : '—';
    },
    canSubmit() {
      if (this.isNewMode) {
        return this.newForm.valid;
      }
      return !this.candidatesLoading && this.destinationId != null;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.reset();
      }
    },
    mode(newVal) {
      this.destinationId = null;
      this.newForm = { ...EMPTY_NEW_FORM };
      if (newVal === MODE_INACTIVE) {
        this.$emit('inactive-mode-selected');
      }
    },
  },
  methods: {
    onNewFormChange(payload) {
      this.newForm = payload;
    },
    onDestinationChange(destinationId) {
      this.destinationId = destinationId;
    },
    submit() {
      if (this.loading || !this.canSubmit) {
        return;
      }
      if (this.isNewMode) {
        this.$emit('relocated', { name: this.newForm.name, copy_share: this.newForm.copyShare });
        return;
      }
      this.$emit('transferred', { destination_id: this.destinationId });
    },
    cancel() {
      this.$emit('input', false);
      this.reset();
    },
    onVisibleChange(val) {
      this.$emit('input', val);
      if (!val) {
        this.reset();
      }
    },
    reset() {
      this.mode = MODE_NEW;
      this.newForm = { ...EMPTY_NEW_FORM };
      this.destinationId = null;
      this.formKey += 1;
    },
  },
};
</script>

<style scoped lang="sass">
.device-relocation
  &__panes
    // 狭い画面では左右 2 ペインを縦積みへ切り替える
    @media (max-width: 700px)
      flex-direction: column

  &__input
    flex: 1 1 58%
    padding: 20px 24px

    @media (max-width: 700px)
      flex: 1 1 auto

  &__notice
    flex: 0 0 42%
    padding: 20px
    background-color: #f5f5f5
    border-left: 1px solid #e0e0e0

    @media (max-width: 700px)
      flex: 1 1 auto
      border-left: none
      border-top: 1px solid #e0e0e0

  &__title
    font-size: 19px
    font-weight: 500
    line-height: 1.4

  &__lead
    font-size: 13px
    line-height: 1.6
    color: #555555

  &__field-label
    font-size: 13px

  // 選択肢は入力欄の見出しと同じ 13px に揃え、丸印も本文になじむ大きさへ落とす
  &__mode
    ::v-deep .v-label
      font-size: 13px

    ::v-deep .v-icon
      font-size: 20px

    ::v-deep .v-input--selection-controls__input
      width: 20px
      height: 20px
      margin-right: 4px

  &__warning
    font-size: 12px
    line-height: 1.7

    // 既定の警告色は薄いオレンジ地に対してコントラストが 2.5:1 程度しかなく、
    // 読みづらいうえに刺激が強い。背景は保ったまま文字だけ濃くする
    ::v-deep .v-alert__content
      color: #e65100

  // 既定の文字色は黒に近く、本文を薄くしたぶん見出しだけが浮くため一段落とす
  &__after-head
    color: #555555

  &__after-list
    padding-left: 18px
    font-size: 12px
    line-height: 1.7
    // 背景のグレーに対して黒が強く出るため、読みやすさは保ちつつ一段落とす
    color: #666666
</style>
